import type { IModalConfig, ICloseModalOptions } from './types'
import { ModalConfig } from './config'
import { ModalStack } from './stack'
import { ModalListeners } from './listeners'
import { AutoShowManager } from './auto-show'
import { ModalOpener } from './opener'
import { ModalCloser } from './closer'
import { ModalUtils } from './utils'

export default class Modals {
  private static instance: Modals
  private modalStack: ModalStack
  private listeners: ModalListeners
  private autoShowManager: AutoShowManager

  private closeModalFromUser(
    modalName: string,
    isModalBack: boolean,
    source: 'trigger' | 'escape'
  ): void {
    const config = ModalConfig.get(modalName)

    if (config?.disableUserClose) {
      config.onTriedClosing?.(source)
      return
    }

    this.closeModal(modalName, isModalBack)
  }

  /**
   * Конструктор класса Modals.
   * Инициализирует модальные окна.
   */
  private constructor() {
    this.modalStack = new ModalStack()
    this.autoShowManager = new AutoShowManager()

    // Создаем обработчики событий
    const onModalClick = ModalListeners.createModalClickHandler(
      () => this.modalStack.getCurrent(),
      (modalName, isModalBack) =>
        this.closeModalFromUser(modalName, isModalBack, 'trigger')
    )

    const onModalKeyboard = ModalListeners.createModalKeyboardHandler(
      () => this.modalStack.getCurrent(),
      (modalName, isModalBack) =>
        this.closeModalFromUser(modalName, isModalBack, 'escape')
    )

    const onTriggerClick = ModalListeners.createTriggerClickHandler(
      (modalName, event) => this.openModal(modalName, event)
    )

    this.listeners = new ModalListeners(
      onModalClick,
      onModalKeyboard,
      onTriggerClick
    )

    this.init()
  }

  /**
   * Инициализирует модальные окна.
   */
  private init(): void {
    ModalUtils.clearPreload()
    this.listeners.initTriggers()
  }

  /**
   * Возвращает единственный экземпляр класса Modals.
   * @return {Modals} Экземпляр класса Modals.
   */
  public static getInstance(): Modals {
    if (!this.instance) {
      this.instance = new Modals()
    }
    return this.instance
  }

  /**
   * Добавляет конфигурацию для модального окна.
   * @param {string} name - Имя модального окна.
   * @param {IModalConfig} config - Конфигурация модального окна.
   * @param {boolean} [force=false] - Принудительно добавить модальное окно, даже если оно уже существует.
   */
  public addModal(
    name: string,
    config: IModalConfig,
    force: boolean = false
  ): void {
    ModalConfig.add(name, config, force)

    if (config.autoShow) {
      this.autoShowManager.setAutoShow(
        name,
        config.autoShowDelay || 0,
        () => !!this.modalStack.getCurrent(),
        (modalName) => this.openModal(modalName)
      )
    }
  }

  /**
   * Удаляет конфигурацию модального окна.
   * @param {string} name - Имя модального окна.
   */
  public removeModal(name: string): void {
    ModalConfig.remove(name)
  }

  /**
   * Приостанавливает автоматический показ модальных окон.
   */
  public pauseAutoShow(): void {
    this.autoShowManager.pause()
  }

  /**
   * Возобновляет автоматический показ модальных окон.
   */
  public resumeAutoShow(): void {
    this.autoShowManager.resume()
  }

  /**
   * Добавляет триггер для открытия модального окна.
   * @param {HTMLElement} trigger - Элемент-триггер.
   */
  public addTrigger(trigger: HTMLElement): void {
    this.listeners.addTrigger(trigger)
  }

  /**
   * Открывает модальное окно с указанным именем.
   * @param {string} modalName - Имя модального окна.
   * @param {MouseEvent} [event] - Событие, вызвавшее открытие.
   */
  public openModal(modalName: string, event?: MouseEvent | null): void {
    ModalOpener.open(
      modalName,
      event,
      this.modalStack,
      this.listeners,
      (options) => this.closeModalImpl(options)
    )
  }

  /**
   * Закрывает указанное модальное окно.
   * @param {string} [modalName] - Имя модального окна.
   * @param {boolean} [isModalBack] - Указывает, закрывается ли окно кнопкой "Назад".
   */
  public closeModal(modalName?: string | null, isModalBack?: boolean): void {
    const name = modalName || this.modalStack.getCurrent()
    if (!name) {
      return
    }
    this.closeModalImpl({ modalName: name, isModalBack })
  }

  /**
   * Внутренняя реализация функции закрытия модального окна.
   * @param {ICloseModalOptions} options - Опции закрытия.
   */
  private closeModalImpl(options: ICloseModalOptions): void {
    ModalCloser.close(options, this.modalStack, this.listeners, (modalName) =>
      this.openModal(modalName)
    )
  }
}
